import URLS from "~/constants/urls"

export const getDomainWithoutPrefix = (url: string) =>
  url?.replace(/^(https?:\/\/)?(www\.)?/, "")

export const sanitizeUrl = (url: string) => {
  if (!url?.startsWith("http") && !url?.startsWith("www")) {
    return `https://${url}`
  }
  return url?.replace(/^(https?:\/\/)?(www\.)?/, "https://www.")
}

export const getCookie = (cookieString: string, cookieName: string) => {
  const cookies = cookieString.split(";").map((cookie) => cookie.trim())
  const cookie = cookies.find((cookie) => cookie.startsWith(`${cookieName}=`))
  return cookie ? cookie.split("=")[1] : null
}

export const toCapitalize = (str: string | string[]) => {
  if (!str) return ""
  return Array.isArray(str)
    ? str
        .filter(Boolean)
        .map((_str) => `${_str[0].toUpperCase()}${_str.slice(1).toLowerCase()}`)
    : `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}`
}

export const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e
  }
  return e?.fileList || []
}

export const clipText = (text: string, count = 5, end = 7) => {
  if (end > text?.length) end = 1
  return text && text.length > 20
    ? `${text.substring(0, count)}...${text.substring(text.length - end)}`
    : text
}

export const asRecord = <T extends Record<string, unknown>>(
  value: T | null | undefined
) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value
  }
  return {}
}

export async function transformRfpInput(data: any) {
  const result = []
  for (const section of data) {
    const { markdownToString } = await import("./markdown-to-string")
    const sectionName = await markdownToString(section.name)
    for (const question of section.questions) {
      result.push({
        Section: sectionName,
        Subsection: "",
        Question: question.question,
        Answer: question.answer || question.shortAnswer
      })
    }
    for (const subsection of section.subHeadings) {
      const subsectionName = await markdownToString(subsection.name)

      for (const question of subsection.questions) {
        const questionText = await markdownToString(question.question)
        const answerText = await markdownToString(question.answer)

        result.push({
          Section: sectionName,
          Subsection: subsectionName,
          Question: questionText,
          Answer: answerText
        })
      }
    }
    if (section.questions.length === 0 && section.subHeadings.length === 0) {
      result.push({
        Section: sectionName,
        Subsection: "",
        Question: "",
        Answer: ""
      })
    }
  }

  return result
}

export function hasAtLeastOneAnswer(arr: any) {
  for (let i = 0; i < arr.headings.length; i++) {
    const heading = arr.headings[i]
    for (let j = 0; j < heading.questions.length; j++) {
      const question = heading.questions[j]
      if (question.answer || question.shortAnswer) {
        return true
      }
    }
    for (let j = 0; j < heading.subHeadings.length; j++) {
      const subHeading = heading.subHeadings[j]
      for (let k = 0; k < subHeading.questions.length; k++) {
        const question = subHeading.questions[k]
        if (question.answer || question.shortAnswer) {
          return true
        }
      }
    }
  }

  return false
}

export function hasAtLeastOneQuestion(arr: any) {
  for (let i = 0; i < arr.headings.length; i++) {
    const heading = arr.headings[i]
    for (let j = 0; j < heading.questions.length; j++) {
      const question = heading.questions[j]
      if (question.question) {
        return true
      }
    }
    for (let j = 0; j < heading.subHeadings.length; j++) {
      const subHeading = heading.subHeadings[j]
      for (let k = 0; k < subHeading.questions.length; k++) {
        const question = subHeading.questions[k]
        if (question.question) {
          return true
        }
      }
    }
  }

  return false
}

export const isValidEnvironment =
  typeof document !== "undefined" && !window.location.href.includes("localhost")

export function chunkArray(array: any[], size: number) {
  const chunkedArray = []
  for (let i = 0; i < array.length; i += size) {
    chunkedArray.push(array.slice(i, i + size))
  }
  return chunkedArray
}

export const removeSpecialCharacters = (str: string, replaceWith = "") => {
  return str.replace(/�/g, replaceWith)
}

export function extractNumber(inputStr: string) {
  if (typeof inputStr === "number") return inputStr
  // eslint-disable-next-line no-useless-escape
  const filteredStr = inputStr.replace(/[^0-9\-_+\.]/g, "")

  const number = parseFloat(filteredStr)
  return number || ""
}

export function removeDuplicates(arr: any[], property?: string) {
  if (!Array.isArray(arr)) return arr

  const seen = new Set()

  return arr.filter((obj) => {
    const value = property ? obj[property] : obj
    if (!seen.has(value)) {
      seen.add(value)
      return true
    }
    return false
  })
}

export const parseRationalString = (input: string) => {
  const lines = input.split("\n")
  const mainText = lines[0]
  const initiatives = lines
    .slice(1)
    .map((line) => {
      const match = line.match(/^- (.+?): (.+)/)
      return match ? { title: match[1], description: match[2] } : null
    })
    .filter(Boolean) as { title: string; description: string }[]

  return { mainText, initiatives }
}

export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf(".")
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return ""
  }
  return filename.slice(lastDotIndex + 1)
}

export const getFileName = (path: string): string => {
  const fileName = path.split("/").pop() || ""
  return fileName
}

export const roundUpToOneDecimal = (num: number) => Math.ceil(num * 10) / 10

export function formatNumber(num: number) {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K"
  } else {
    return num?.toString()
  }
}

export const formatDuration = (totalSeconds: number) => {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0")
  const seconds = String(totalSeconds % 60).padStart(2, "0")
  return `${minutes}:${seconds}`
}

export function replaceCloudfrontUrl(originalUrl: string, tenantId: number) {
  if (tenantId !== 16) {
    return originalUrl
  }
  const [, ...rest] = originalUrl
    .replace(/^(https?:\/\/)?(www\.)?/, "")
    .split("/")

  return `${URLS.ASSETS}/tenant/${tenantId}/${rest.join("/")}`
}

export const isHTML = (input: string) => {
  if (!input) return ""
  return /^<\/?[a-z][\s\S]*>/i.test(input.trim())
}

export const filterOption = (inputText: string, option: any) => {
  return (
    option?.label?.toString().toLowerCase().includes(inputText.toLowerCase()) ||
    option?.value?.toString().toLowerCase().includes(inputText.toLowerCase())
  )
}

const DEFAULT_FILE_TYPE = { type: "FILE", color: "bg-gray-500" }

export const FILE_TYPE_MAP = new Map([
  ["pdf", { type: "PDF", color: "bg-red-500" }],
  ["doc", { type: "DOC", color: "bg-blue-500" }],
  ["docx", { type: "DOC", color: "bg-blue-500" }],
  ["xls", { type: "XLS", color: "bg-green-500" }],
  ["xlsx", { type: "XLS", color: "bg-green-500" }],
  ["ppt", { type: "PPT", color: "bg-orange-500" }],
  ["pptx", { type: "PPT", color: "bg-orange-500" }]
])

export const getFileTypeAndIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase()
  return FILE_TYPE_MAP.get(extension || "") || DEFAULT_FILE_TYPE
}

export const formatCurrency = (
  amount: number,
  style: "currency" | "decimal" = "currency",
  currency: string = "USD"
) => {
  if (!Number.isFinite(amount)) return "-"
  return new Intl.NumberFormat("en-US", {
    style,
    currency,
    maximumFractionDigits: 0
  }).format(amount)
}
